/* eslint-disable max-len */
/**
 * @fileoverview Shared template and format constants for the content workflow.
 *               Single source of truth for formats, platform templates, tones, and text depth options.
 *               PATH: src/components/shared/constants/templateConstants.js
 */

// ---------------------------------------------------------------------------
// FORMAT DEFINITIONS (platform-agnostic)
// ---------------------------------------------------------------------------

/**
 * Master list of content formats. Each entry describes a format independent of
 * which platform it will be published on. Platform-specific template IDs are
 * derived at runtime via `getTemplateIdsForFormat()`.
 */
export const FORMATS = [
    { id: 'standard', label: 'Standard Post', iconName: 'FiLayout', desc: 'Classic image + text. The workhorse of social media.', mediaType: 'image', isVertical: false, isEphemeral: false, supportedWorkflows: ['single', 'stream'] },
    { id: 'carousel', label: 'Carousel', iconName: 'FiLayers', desc: 'Swipeable multi-slide storytelling. High engagement.', mediaType: 'image', isVertical: false, isEphemeral: false, supportedWorkflows: ['single', 'stream'] },
    { id: 'reel', label: 'Reel', iconName: 'FiVideo', desc: 'Short-form vertical video. Maximum reach.', mediaType: 'video', isVertical: true, isEphemeral: false, supportedWorkflows: ['single', 'stream'] },
    { id: 'story_image', label: 'Story (Image)', iconName: 'FiCamera', desc: 'Full-screen image that disappears after 24h.', mediaType: 'image', isVertical: true, isEphemeral: true, supportedWorkflows: ['single', 'stream'] },
    { id: 'story_video', label: 'Story (Video)', iconName: 'FiFilm', desc: 'Full-screen video clip that disappears after 24h.', mediaType: 'video', isVertical: true, isEphemeral: true, supportedWorkflows: ['single', 'stream'] },
];

// ---------------------------------------------------------------------------
// PLATFORM TEMPLATE DEFINITIONS (platform-specific, used by backend routing)
// ---------------------------------------------------------------------------

export const PLATFORM_PREFIXES = {
    facebook: 'fb_',
    instagram: 'ig_',
    linkedin: 'li_',
};

/**
 * Facebook-specific templates. Each carries the full `fb_` prefixed ID and
 * platform-tuned descriptions. The `icon` field stores a React-icon component
 * name (string) — the consuming component renders the actual icon.
 */
export const FB_TEMPLATES = [
    { id: 'fb_standard', label: 'Standard Post', iconName: 'FiLayout', desc: 'Image + Text. Optimized hook (40-80 chars) for max engagement.', mediaType: 'image', isVertical: false, isEphemeral: false },
    { id: 'fb_carousel', label: 'Carousel (Slide Deck)', iconName: 'FiLayers', desc: 'Multi-slide narrative. High retention & engagement.', mediaType: 'image', isVertical: false, isEphemeral: false },
    { id: 'fb_reel', label: 'Facebook Reel', iconName: 'FiVideo', desc: '9:16 Video. High viral potential. Short description.', mediaType: 'video', isVertical: true, isEphemeral: false },
    { id: 'fb_story_image', label: 'Story (Image)', iconName: 'FiCamera', desc: '9:16 Image Story. Disappears after 24h. Great for time-sensitive content.', mediaType: 'image', isVertical: true, isEphemeral: true },
    { id: 'fb_story_video', label: 'Story (Video)', iconName: 'FiFilm', desc: '9:16 Video Story. Disappears after 24h. Up to 60s.', mediaType: 'video', isVertical: true, isEphemeral: true },
];

/**
 * Instagram-specific templates.
 */
export const IG_TEMPLATES = [
    { id: 'ig_standard', label: 'Standard Post', iconName: 'FiLayout', desc: '1:1 Image + Narrative Caption. 5-10 hashtags.', mediaType: 'image', isVertical: false, isEphemeral: false },
    { id: 'ig_carousel', label: 'Carousel (Slide Deck)', iconName: 'FiLayers', desc: 'Swipeable storytelling (4:5). The algorithm loves this.', mediaType: 'image', isVertical: false, isEphemeral: false },
    { id: 'ig_reel', label: 'Instagram Reel', iconName: 'FiVideo', desc: '9:16 Video + Hook Caption. The engagement winner.', mediaType: 'video', isVertical: true, isEphemeral: false },
    { id: 'ig_story_image', label: 'Story (Image)', iconName: 'FiCamera', desc: '9:16 Image Story. Disappears after 24h. Drives urgency.', mediaType: 'image', isVertical: true, isEphemeral: true },
    { id: 'ig_story_video', label: 'Story (Video)', iconName: 'FiFilm', desc: '9:16 Video Story. Disappears after 24h. Up to 60s.', mediaType: 'video', isVertical: true, isEphemeral: true },
];

/**
 * LinkedIn-specific templates. LinkedIn has no stories or reels — only standard posts and carousels.
 */
export const LI_TEMPLATES = [
    { id: 'li_standard', label: 'Standard Post', iconName: 'FiLayout', desc: 'Professional image + text. Thought leadership optimized.', mediaType: 'image', isVertical: false, isEphemeral: false },
    { id: 'li_carousel', label: 'Carousel (Document)', iconName: 'FiLayers', desc: 'Multi-image professional storytelling. High engagement on LinkedIn.', mediaType: 'image', isVertical: false, isEphemeral: false },
];

// ---------------------------------------------------------------------------
// TONE & TEXT DEPTH
// ---------------------------------------------------------------------------

export const TONES = [
    'Inherit Project Tone',
    'Professional',
    'Friendly',
    'Inspirational',
    'Playful',
    'Bold',
    'Casual',
    'Authoritative',
    'Witty',
];

export const LONGEVITY_OPTIONS = [
    { id: 'short', label: 'Short', iconName: 'FiMoreHorizontal', desc: 'Quick and punchy. 1-2 sentences.' },
    { id: 'medium', label: 'Medium', iconName: 'FiMenu', desc: 'The sweet spot. Engaging and balanced.' },
    { id: 'long', label: 'Long', iconName: 'FiAlignLeft', desc: 'The full story. A deep-dive narrative.' },
];

// ---------------------------------------------------------------------------
// STREAM FREQUENCIES
// ---------------------------------------------------------------------------

export const FREQUENCIES = [
    { value: 'daily', label: 'Daily' },
    { value: 'every_second_day', label: 'Every Second Day' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
];

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------

/**
 * Builds a platform-specific template ID from a format and platform.
 * e.g. getTemplateIdForFormat('carousel', 'facebook') → 'fb_carousel'
 */
export function getTemplateIdForFormat(formatId, platform) {
    const prefix = PLATFORM_PREFIXES[platform];
    if (!prefix) return null;
    return `${prefix}${formatId}`;
}

/**
 * Builds template IDs for all selected platforms.
 * e.g. getTemplateIdsForFormat('standard', ['facebook', 'instagram'])
 *   → { facebook: 'fb_standard', instagram: 'ig_standard' }
 */
export function getTemplateIdsForFormat(formatId, platforms) {
    const templates = {};
    for (const platform of platforms) {
        templates[platform] = getTemplateIdForFormat(formatId, platform);
    }
    return templates;
}

/**
 * Extracts the base format ID from a platform-prefixed template ID.
 * e.g. getFormatFromTemplateId('ig_story_image') → 'story_image'
 */
export function getFormatFromTemplateId(templateId) {
    if (!templateId) return null;
    return templateId.replace(/^(fb|ig|li)_/, '');
}

/**
 * Returns the full format config object for a given format ID.
 * e.g. getFormatConfig('carousel') → { id: 'carousel', label: 'Carousel', ... }
 */
export function getFormatConfig(formatId) {
    return FORMATS.find(f => f.id === formatId) || null;
}

/**
 * Checks whether a given format supports a specific workflow type.
 * e.g. isFormatSupportedForWorkflow('carousel', 'stream') → false
 */
export function isFormatSupportedForWorkflow(formatId, workflow) {
    const config = getFormatConfig(formatId);
    if (!config) return false;
    return config.supportedWorkflows.includes(workflow);
}
